import React, { useState, useEffect, useRef } from 'react';
import { adminGetProducts, adminAddProduct, adminUpdateProduct, adminDeleteProduct } from '../../api/admin';
import AdminLayout from './AdminLayout';

const EMPTY_FORM = { id: null, name: '', category: '', description: '', price: '', sort_order: 0, is_active: 1 };

function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [form, setForm]         = useState(EMPTY_FORM);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving]     = useState(false);
  const [msg, setMsg]           = useState('');
  const fileRef = useRef();

  const load = () => {
    setLoading(true);
    adminGetProducts().then(res => {
      if (res.success) setProducts(res.data);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openAdd  = () => { setForm(EMPTY_FORM); setImageFile(null); setImagePreview(''); setShowForm(true); setMsg(''); };
  const openEdit = (p) => { setForm({ ...p }); setImageFile(null); setImagePreview(p.image_url || ''); setShowForm(true); setMsg(''); };
  const closeForm = () => { setShowForm(false); setMsg(''); };

  const handleImage = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setMsg('Product name is required'); return; }
    setSaving(true); setMsg('');
    try {
      let res;
      if (form.id) {
        res = await adminUpdateProduct({ ...form });
      } else {
        const fd = new FormData();
        Object.entries(form).forEach(([k, v]) => { if (v !== null) fd.append(k, v); });
        if (imageFile) fd.append('image', imageFile);
        res = await adminAddProduct(fd);
      }
      if (res.success) { setMsg('✅ Saved!'); load(); setTimeout(closeForm, 1000); }
      else setMsg('❌ ' + res.message);
    } catch { setMsg('❌ Network error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    await adminDeleteProduct(id);
    load();
  };

  const handleToggle = async (p) => {
    await adminUpdateProduct({ ...p, is_active: p.is_active ? 0 : 1 });
    load();
  };

  return (
    <AdminLayout title="Products">
      <div className="adm-page-header">
        <h2>Manage Products</h2>
        <button className="adm-btn-primary" onClick={openAdd}>➕ Add Product</button>
      </div>

      {showForm && (
        <div className="adm-modal-overlay" onClick={closeForm}>
          <div className="adm-modal" onClick={e => e.stopPropagation()}>
            <div className="adm-modal__header">
              <h3>{form.id ? 'Edit Product' : 'Add New Product'}</h3>
              <button onClick={closeForm}>✕</button>
            </div>
            <div className="adm-modal__body">
              {msg && <div className={`adm-msg ${msg.startsWith('✅') ? 'adm-msg--success' : 'adm-msg--error'}`}>{msg}</div>}

              <div className="adm-form-row">
                <div className="adm-form-field">
                  <label>Product Name *</label>
                  <input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Product name" />
                </div>
                <div className="adm-form-field">
                  <label>Category</label>
                  <input value={form.category} onChange={e => setForm({...form, category: e.target.value})} placeholder="e.g. Artificial Grass, Plants" />
                </div>
              </div>

              <div className="adm-form-field">
                <label>Price (optional)</label>
                <input value={form.price} onChange={e => setForm({...form, price: e.target.value})} placeholder="e.g. ₹500/sq ft or Contact for price" />
              </div>

              <div className="adm-form-field">
                <label>Description</label>
                <textarea rows={4} value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Product details..." />
              </div>

              <div className="adm-form-row">
                <div className="adm-form-field">
                  <label>Sort Order</label>
                  <input type="number" value={form.sort_order} onChange={e => setForm({...form, sort_order: e.target.value})} />
                </div>
                <div className="adm-form-field">
                  <label>Status</label>
                  <select value={form.is_active} onChange={e => setForm({...form, is_active: parseInt(e.target.value)})}>
                    <option value={1}>Active</option>
                    <option value={0}>Inactive</option>
                  </select>
                </div>
              </div>

              <div className="adm-form-field">
                <label>Product Image</label>
                <div className="adm-upload-box" onClick={() => fileRef.current.click()}>
                  {imagePreview
                    ? <img src={imagePreview} alt="preview" className="adm-upload-preview" />
                    : <div className="adm-upload-placeholder">📷 Click to upload image</div>
                  }
                </div>
                <input ref={fileRef} type="file" accept="image/*" style={{display:'none'}} onChange={handleImage} />
              </div>
            </div>
            <div className="adm-modal__footer">
              <button className="adm-btn-secondary" onClick={closeForm}>Cancel</button>
              <button className="adm-btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save Product'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="adm-section">
        {loading ? <div className="adm-loading">Loading...</div> : (
          <div className="adm-table-wrap">
            <table className="adm-table">
              <thead>
                <tr><th>Image</th><th>Name</th><th>Category</th><th>Price</th><th>Status</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {products.map(p => (
                  <tr key={p.id}>
                    <td>
                      {p.image_url
                        ? <img src={p.image_url} alt={p.name} className="adm-thumb" />
                        : <span className="adm-no-img">📦</span>
                      }
                    </td>
                    <td><strong>{p.name}</strong></td>
                    <td>{p.category}</td>
                    <td>{p.price || '—'}</td>
                    <td>
                      <button className={`adm-toggle ${p.is_active ? 'adm-toggle--on' : 'adm-toggle--off'}`} onClick={() => handleToggle(p)}>
                        {p.is_active ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td>
                      <div className="adm-actions">
                        <button className="adm-btn-edit" onClick={() => openEdit(p)}>✏️ Edit</button>
                        <button className="adm-btn-delete" onClick={() => handleDelete(p.id, p.name)}>🗑️</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

export default AdminProducts;